import styled from "styled-components";
import { Button } from "melodies-source/Button";
import { Modal } from "melodies-source/Modal";
import { DefaultAlbum } from "Routes/SetFan/Components/SongsQuestion/SortableRow";
import useSurveyQuestions from "hooks/useSurveyQuestions";
import { useTypesenseDocuments } from "hooks/useTypesenseDocument";
import { Typesense } from "@max/common/typesense";
import { SweepsEntry } from "@max/common";
import { Fan, Gender } from "@max/common/dist/artists/fans";
import { useArtistContext } from "Components";
import { useState } from "react";
import { ReactComponent as PhoneIcon } from "assets/svg/phone.svg";
import { ReactComponent as EmailIcon } from "assets/svg/email.svg";
import Skeleton from "react-loading-skeleton";
import { getPiiData, PiiKey } from "Utils/secure";
import { FlexRow } from "Components/Flex";

export const WinnerDetailModal = ({
  onClose,
  isOpen,
  surveyId,
  winnerDetails,
}: {
  onClose: () => void;
  isOpen: boolean;
  surveyId: string;
  winnerDetails: SweepsEntry & {
    id: string;
    isRemoved: boolean;
    displayName: string;
  };
}) => {
  const { isAdmin, id: artistGroupId } = useArtistContext();
  const { surveyQuestions } = useSurveyQuestions(surveyId);
  const { documents: submissions, loading: loadingSubmissions } =
    useTypesenseDocuments({
      collection: "submissions",
      filter_by: `id:=[${surveyId}#${winnerDetails.id}]`,
      limit: 1,
    });
  const { documents: contacts, loading: loadingContacts } =
    useTypesenseDocuments({
      collection: "contacts",
      filter_by: `id:=[${artistGroupId}:${winnerDetails.id}]`,
      limit: 1,
    });

  // TODO: this will work for now, but we should really show a loading state
  // in modal, so that a user can continue to use the UI like normal but with loading states.
  // currently, this will block the modal from opening until load
  if (
    loadingSubmissions ||
    loadingContacts ||
    !submissions?.length ||
    !contacts?.length
  ) {
    return null;
  }

  const submission: Typesense<"submissions"> = submissions[0];

  const winnerContact: Typesense<"contacts"> | undefined = contacts?.[0];
  const winnerGender = isValidGenderKey(winnerContact?.gender)
    ? Gender[winnerContact.gender]
    : undefined;
  const winnerAge =
    typeof winnerContact?.age === "number" ? winnerContact.age : undefined;
  const winnerLocation = winnerContact.location ?? winnerDetails.countryCode;

  return (
    <Modal header="Response Detail" isOpen={isOpen} onClose={onClose}>
      <Scrollable>
        <NameText>{winnerDetails.displayName}</NameText>
        {(isAdmin || !!winnerContact.email) && (
          <BodyText>
            Email:
            <SecurePIIField
              fanId={winnerDetails.id}
              fallback={winnerContact.email}
              piiType="email"
            />
          </BodyText>
        )}
        {winnerDetails.canSms && (isAdmin || !!winnerContact.phone) && (
          <BodyText>
            Phone:
            <SecurePIIField
              fanId={winnerDetails.id}
              fallback={winnerContact.phone}
              piiType="phone"
            />
          </BodyText>
        )}
        {!!winnerLocation && <BodyText>Location: {winnerLocation}</BodyText>}
        {!!winnerGender && <BodyText>Gender: {winnerGender}</BodyText>}
        {!!winnerAge && <BodyText>Age: {winnerAge}</BodyText>}
        <Divider />
        {Object.entries(surveyQuestions).map(([questionId, question]) => {
          let winnerResponse: string | string[] = "";
          if (question.type === "TextQuestion") {
            submission.free_responses.forEach((freeResponse) => {
              if (freeResponse.startsWith(questionId)) {
                winnerResponse = freeResponse.slice(questionId.length);
              }
            });
          } else if (
            question.type === "MultipleSelectQuestion" ||
            question.type === "SongsQuestion"
          ) {
            winnerResponse = [];
            submission.responses.forEach((response) => {
              if (response.startsWith(questionId)) {
                (winnerResponse as string[]).push(response.split("#")[1]);
              }
            });
          } else {
            submission.responses.forEach((response) => {
              if (response.startsWith(questionId)) {
                winnerResponse = response.split("#")[1];
              }
            });
          }

          // if the question is not required we might not have a response for it
          if (!winnerResponse) {
            return (
              <QuestionRow key={`${questionId}-${winnerResponse.slice(0, 10)}`}>
                <QuestionLabel>{question.label}: </QuestionLabel>
                No response given
              </QuestionRow>
            );
          }

          let AnswerComponent: JSX.Element;
          switch (question.type) {
            case "TextQuestion":
              AnswerComponent = <BodyText>{winnerResponse}</BodyText>;
              break;
            case "ImageQuestion":
              AnswerComponent = (
                <img
                  width={75}
                  src={
                    surveyQuestions[questionId].options[
                      winnerResponse as string
                    ].src
                  }
                  alt="question answer"
                />
              );
              break;
            case "SongsQuestion":
              AnswerComponent = (
                <>
                  {(winnerResponse as string[]).map((resp) => {
                    const song = surveyQuestions[questionId].options[resp];
                    return (
                      <div
                        key={resp}
                        style={{
                          display: "flex",
                          marginBottom: 6,
                          alignItems: "center",
                        }}
                      >
                        {song.src ? (
                          <img
                            src={song.src}
                            style={{ width: 33, height: 33, marginRight: 6 }}
                            alt={song.label}
                          />
                        ) : (
                          <StyledDefaultAlbum />
                        )}
                        <div>{song.label}</div>
                      </div>
                    );
                  })}
                </>
              );
              break;
            case "MultipleSelectQuestion":
              AnswerComponent = (
                <ol style={{ listStyle: "inside" }}>
                  {(winnerResponse as string[]).map((resp) => {
                    const questionOption =
                      surveyQuestions[questionId].options[resp];
                    const label = questionOption.userDefined
                      ? `"Other"`
                      : questionOption.label;
                    return <li key={resp}>{label}</li>;
                  })}
                </ol>
              );
              break;
            default:
              AnswerComponent = (
                <BodyText>
                  {
                    surveyQuestions[questionId].options[
                      winnerResponse as string
                    ].label
                  }
                </BodyText>
              );
              break;
          }
          return (
            <QuestionRow key={`${questionId}-${winnerResponse.slice(0, 10)}`}>
              <QuestionLabel>{question.label}: </QuestionLabel>
              {AnswerComponent}
            </QuestionRow>
          );
        })}
      </Scrollable>
      <ButtonContainer>
        <Button onClick={onClose} style={{ width: 156 }}>
          Close
        </Button>
      </ButtonContainer>
    </Modal>
  );
};

const SecurePIIField = ({
  fanId,
  fallback,
  piiType,
}: {
  fanId: string;
  fallback: string;
  piiType: PiiKey;
}) => {
  const { id: artistGroupId } = useArtistContext();
  const [loading, setLoading] = useState(false);
  const [field, setField] = useState("");

  const handlePiiDataClick = async () => {
    const value = await getPiiData(
      artistGroupId,
      piiType,
      fanId,
      setLoading,
    ).catch((err) => {
      // this is not necessarily an error, we could just be unauthorized for decryption
      console.error(
        "there was a problem getting pii data",
        piiType,
        fanId,
        err,
      );
      return undefined;
    });
    // if api call fails (unauthorized, value not present) set the fallback
    setField(value || fallback);
  };

  const Icon = piiType === "phone" ? PhoneIcon : EmailIcon;

  return (
    <PiiWrapper data-sentry-mask style={{ marginLeft: 5 }}>
      <PiiData>
        {loading ? (
          <Skeleton width={125} />
        ) : !field ? (
          <Icon onClick={async () => await handlePiiDataClick()} />
        ) : (
          <span>{field}</span>
        )}
      </PiiData>
    </PiiWrapper>
  );
};

const StyledDefaultAlbum = styled(DefaultAlbum)`
  margin-left: 0px !important;

  & > svg {
    top: 2px;
    left: 7px;
    width: 18px;
  }

  & > img {
    width: 33px;
    height: 33px;
    margin-right: 6px;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const Scrollable = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  margin-bottom: 20px;

  ${({ theme }) => theme.media.desktop} {
    margin-bottom: 40px;
  }
`;

const QuestionRow = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const NameText = styled.div`
  color: #333;
  font-family: Poppins;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  margin-top: 30px;
`;

const BodyText = styled(FlexRow)`
  color: #333;
  font-family: Poppins;
  font-size: 15px;
  line-height: 22px;
  flex-wrap: wrap;
`;

const Divider = styled.div`
  width: 374px;
  height: 1px;
  background: #cdcdcd;
  margin-top: 30px;
  margin-bottom: 30px;
`;

const QuestionLabel = styled.div`
  color: #999;
  font-family: Poppins;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;

const isValidGenderKey = (key: unknown): key is Fan["gender"] =>
  typeof key === "string" && key in Gender;

const PiiData = styled.div`
  display: flex;
  width: 100%;

  ${({ theme }) => theme.media.desktop} {
    height: 24px;

    span {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }

  svg {
    cursor: pointer;

    height: 18px;
    width: 18px;

    ${({ theme }) => theme.media.mobile} {
      height: 23px;
      width: 23px;
      padding: 1px 0;
    }
  }
`;

const PiiWrapper = styled.div`
  color: inherit;
  font-family: Poppins;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 21px;
`;
